import React from 'react';
import {
  Box,
  Grid,
  LinearProgress,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import BillingAndUsageInvoices from './BillingAndUsageInvoices';
import SubscriptionModal from './SubscriptionModal';
import BillingAndUsageSkeleton from 'sharedComponents/Skeleton/BillingAndUsageSkeleton';
import UsageCirclesGrid from './UsageCirclesGrid';
import { SparkButton } from 'sharedComponents/UI';
import { useCurrentUserAccount } from 'hooks/Account';
import { useSubscriptionTypes } from 'hooks/EntityTypes/useSubscriptioonType';
import { useSubscriptionCreditOptionsByID } from 'hooks/EntityTypes/useSubscriptionCreditOptionsByID';
import { useStripeInvoices } from 'hooks/Stripe';
import { useMarketplaceAccount } from 'hooks/Marketplace/useMarketPlaceAccount';
import { formatDateAndTime } from 'utilities/time';
import { STRIPE_URL } from 'constants/common';
import { useStripeAccountPaymentMethods } from 'hooks/Stripe/useStripeAccountPaymentMethods';
import { capitalizeFirstLetter } from 'utilities/index';
import { useDialog } from 'hooks/common';
import { useSearchParams } from 'react-router-dom';
import { SparkImageLoader } from 'sharedComponents/UI';
import type { UsageData } from './UsageCirclesGrid';

const getFormattedExpirationDate = (month: number, year: number) => {
  if (!month || !year) {
    return;
  }
  const formattedMonth = month < 10 ? `0${month}` : month;
  const formattedYear = year % 100;
  return `${formattedMonth}/${formattedYear}`;
};

function BillingAndUsage() {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchParams] = useSearchParams();

  const { account, isLoading: isLoadingAccount } = useCurrentUserAccount();
  const { marketplaceAccounts, isLoading: isLoadingMarketplace } =
    useMarketplaceAccount(account?.id);
  const marketplaceAccountID = marketplaceAccounts?.[0].id;
  const { data: stripeInvoices, isLoading: isLoadingStripeInvoices } =
    useStripeInvoices(marketplaceAccountID);

  const {
    nameByIdMap: subscriptionTypeNameByIdMap,
    isLoading: isLoadingSubscriptionType,
  } = useSubscriptionTypes();

  const {
    subscriptionCreditOption,
    isLoading: isLoadingSubscriptionCreditOptions,
  } = useSubscriptionCreditOptionsByID(
    account?.attributes.subscriptionCreditOptionID
  );

  const { defaultPaymentMethod, isLoading: isLoadingPaymentMethods } =
    useStripeAccountPaymentMethods(account?.id);
  const {
    open: isSubscriptionModalOpen,
    openDialog: openSubscriptionModal,
    closeDialog: closeSubscriptionModal,
  } = useDialog();

  React.useEffect(() => {
    const subscriptionPopup =
      searchParams.get('subscriptionPopup') === '1' ? true : false;
    if (subscriptionPopup && !isSubscriptionModalOpen) {
      openSubscriptionModal();
      searchParams.set('subscriptionPopup', '0');
    }
  }, [isSubscriptionModalOpen, openSubscriptionModal, searchParams]);

  const subscriptionTypeID =
    subscriptionCreditOption?.attributes.subscriptionTypeID;
  const subscriptionCreditOptionsID = subscriptionCreditOption?.id;
  const subscriptionType =
    subscriptionTypeNameByIdMap?.[subscriptionTypeID || ''];
  const accountMeta = account?.meta;
  const accountUsage = accountMeta?.subscription?.credits.usage;
  const remainingCreditsForCurrentMonth =
    accountMeta?.subscription?.credits?.usage
      ?.remainingCreditsForCurrentInterval || 0;

  const totalCreditsIssuedLastInterval =
    accountMeta?.subscription?.credits?.totalCreditsIssuedLastInterval || 0;
  const currentIntervalUsage = accountUsage?.currentIntervalUsage || 0;
  const accountUsagePercentage = totalCreditsIssuedLastInterval
    ? (currentIntervalUsage / totalCreditsIssuedLastInterval) * 100
    : 0;

  const accountMetaCredits = accountMeta?.subscription?.credits;

  const usageData: UsageData[] = React.useMemo(() => {
    const usageArray = accountMeta?.subscription?.usage;
    if (!usageArray || !Array.isArray(usageArray)) return [];

    return usageArray.map((usage) => ({
      limit: usage.limit,
      count: usage.count,
      percentageUsed: usage.limit ? (usage.count / usage.limit) * 100 : null,
      percentageUsedColour:
        usage.percentagUsedColour || theme.palette.primary.main,
      label: usage.label,
      description: usage.description,
    }));
  }, [accountMeta?.subscription, theme.palette.primary.main]);

  const isLoading =
    isLoadingAccount ||
    isLoadingSubscriptionCreditOptions ||
    isLoadingMarketplace ||
    isLoadingSubscriptionType ||
    isLoadingPaymentMethods ||
    isLoadingStripeInvoices;

  if (isLoading) {
    return <BillingAndUsageSkeleton />;
  }

  return (
    <Grid container padding={3}>
      <Grid container item alignItems='center' mb={isSmallScreen ? 2 : 4}>
        <Typography
          variant='h4'
          sx={{
            color: '#404040',
            fontFamily: 'Mont-semibold',
          }}
        >
          Billing & Usage
        </Typography>

        <Grid
          container
          sx={{
            marginTop: isSmallScreen ? '10px' : '20px',
            backgroundColor: '#ffffff',
            padding: isSmallScreen ? '10px' : '20px',
            borderRadius: '20px',
            border: '1px solid #EEEEEE',
            justifyContent: 'flex-end',
          }}
        >
          <Typography
            variant='h5'
            sx={{
              fontFamily: 'Mont-semibold',
              marginBottom: isSmallScreen ? '5px' : '10px',
              flex: 1,
            }}
          >
            {subscriptionType}
          </Typography>
          <Typography
            sx={{
              fontSize: isSmallScreen ? '16px' : '18px',
              color: theme.palette.grey[500],
              mt: 0.5,
            }}
          >
            {remainingCreditsForCurrentMonth} credits remaining this month
          </Typography>
          <LinearProgress
            sx={{
              width: '100%',
              height: isSmallScreen ? '8px' : '15px',
              borderRadius: '10px',
              mb: 1.5,
              backgroundColor: (theme) => theme.palette.primary.light,
              '& .MuiLinearProgress-bar': {
                backgroundColor: '#FFC000',
              },
              transition: 'all 0.3s ease',
            }}
            variant='determinate'
            value={accountUsagePercentage}
          />

          {accountMetaCredits?.nextIssueDate && (
            <Grid
              item
              xs={12}
              sx={{
                marginBottom: isSmallScreen ? '4px' : '8px',
              }}
            >
              <Typography
                sx={{
                  fontSize: isSmallScreen ? '16px' : '18px',
                  color: theme.palette.grey[500],
                }}
              >
                Your credits reset on
                <span style={{ color: theme.palette.grey[500] }}>
                  {formatDateAndTime(
                    accountMetaCredits?.nextIssueDate,
                    ' D MMM YYYY h:mm A '
                  )}
                </span>
              </Typography>
            </Grid>
          )}

          {usageData.length > 0 && (
            <Grid item xs={12}>
              <UsageCirclesGrid usageData={usageData} />
            </Grid>
          )}

          <SparkButton
            variant='contained'
            sx={{
              width: isSmallScreen ? '100%' : '200px',
              padding: '10px 20px',
              marginTop: '10px',
            }}
            onClick={openSubscriptionModal}
          >
            Change Plan
          </SparkButton>
        </Grid>
      </Grid>
      {!!defaultPaymentMethod && (
        <Grid container item alignItems='center' width='100%'>
          <Typography
            variant='h5'
            sx={{
              fontFamily: 'Mont-semibold',
            }}
          >
            Payment Information
          </Typography>

          <Grid
            container
            alignItems='center'
            sx={{
              marginTop: isSmallScreen ? '10px' : '20px',
              marginBottom: isSmallScreen ? '10px' : '20px',
              backgroundColor: '#ffffff',
              borderRadius: '20px',
              border: '1px solid #EEEEEE',
              justifyContent: 'flex-end',
              padding: 2,
              paddingLeft: 3,
            }}
          >
            <Box display='flex' alignItems='center'>
              <SparkImageLoader
                width={isSmallScreen ? '60' : 'auto'}
                height={isSmallScreen ? '60' : 'auto'}
                src={defaultPaymentMethod.cardDetails.iconURL}
                alt='Card logo'
              />
            </Box>
            <Box display='flex' alignItems='center' flex={1}>
              <Typography
                sx={{
                  fontSize: isSmallScreen ? '16px' : '18px',
                  margin: '10px',
                }}
              >
                {capitalizeFirstLetter(defaultPaymentMethod.cardDetails.brand)}{' '}
                ending in {defaultPaymentMethod.cardDetails.last4}
                <Typography
                  sx={{
                    fontSize: isSmallScreen ? '14px' : '16px',
                    color: theme.palette.grey[500],
                  }}
                >
                  Expires{' '}
                  {getFormattedExpirationDate(
                    defaultPaymentMethod.cardDetails.expMonth,
                    defaultPaymentMethod.cardDetails.expYear
                  )}
                </Typography>
              </Typography>
            </Box>

            <SparkButton
              variant='contained'
              onClick={() =>
                window.open(
                  `${STRIPE_URL.EDIT_PAYMENT}${encodeURIComponent(
                    account?.attributes?.email || ''
                  )}`
                )
              }
              sx={{
                width: isSmallScreen ? '100%' : '200px',
                padding: '10px 20px',
                marginTop: isSmallScreen ? 2 : 0,
              }}
            >
              Edit Payment
            </SparkButton>
          </Grid>
        </Grid>
      )}
      {stripeInvoices && (
        <BillingAndUsageInvoices stripeInvoices={stripeInvoices} />
      )}
      {isSubscriptionModalOpen && (
        <SubscriptionModal
          open={isSubscriptionModalOpen}
          close={closeSubscriptionModal}
          subscriptionTypeID={subscriptionTypeID}
          accountID={account?.id}
          subscriptionCreditOptionsID={subscriptionCreditOptionsID}
        />
      )}
    </Grid>
  );
}

export default BillingAndUsage;
